import { z } from "zod";
import { dateOnlySchema, emailSchema, timestampSchema, uuidSchema } from "./api.ts";
import { timezoneSchema } from "./common.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * LICENSING — the one rule for "may this workspace still change anything?".
 *
 * A workspace's licence is a term: it starts on a date, ends on a date (or
 * never), and is followed by a grace period of a few days. Inside the term and
 * the grace the workspace works normally; after the grace it is READ-ONLY
 * until Lean2Automate renews it. A pause by Lean2Automate (suspension) and a
 * cancellation are read-only too, whatever the dates say.
 *
 * The rule lives here, pure, so the request guard, the hourly sweep, the
 * console, the client's Plan & billing page and its banner cannot disagree
 * about it. The server evaluates it on every request from the stored dates;
 * the stored `status` is a convenience for lists and reports, never the
 * authority.
 *
 * Dates follow one convention throughout. `endsOn = D` means valid THROUGH D
 * in the workspace's own time zone, stored as `endsAt` = the first instant of
 * D+1 there. Every computed end goes through the helpers below, so it always
 * falls on a day boundary and "+1 year" always records a one-year term.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

/**
 * The stored status.
 *
 *   trialing  — inside a trial term
 *   active    — inside a paid term, or open-ended
 *   past_due  — the term has ended; inside the grace period, still writable
 *   expired   — past the grace period; read-only until renewed
 *   suspended — paused by Lean2Automate; read-only whatever the dates say
 *   cancelled — read-only for good
 */
export const SUBSCRIPTION_STATUSES = ["trialing", "active", "past_due", "expired", "suspended", "cancelled"] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];
/** "none": the workspace has no subscription row at all (writable; every workspace that predates the tier). */
export const subscriptionStatusSchema = z.enum([...SUBSCRIPTION_STATUSES, "none"]);

/** What a person is shown, derived from the dates on every read. */
export const LICENCE_PHASES = ["none", "trial", "active", "expiring", "grace", "expired", "paused", "cancelled"] as const;
export type LicencePhase = (typeof LICENCE_PHASES)[number];
export const licencePhaseSchema = z.enum(LICENCE_PHASES);

export const READ_ONLY_REASONS = ["paused", "cancelled", "expired"] as const;
export type ReadOnlyReason = (typeof READ_ONLY_REASONS)[number];

/** A paid licence is "expiring" for its last seven days; the first notice goes out then. */
export const LICENCE_WARNING_DAYS = 7;
/** Grace a new licence gets unless somebody chooses otherwise. */
export const DEFAULT_GRACE_DAYS = 7;
/** Length of the trial a self-signed-up workspace starts on. */
export const DEFAULT_TRIAL_DAYS = 14;

const DAY_MS = 86_400_000;

// ---------------------------------------------------------------------------
// licenceState — the rule
// ---------------------------------------------------------------------------

export interface LicenceStateInput {
  status: SubscriptionStatus | "none" | string;
  isTrial: boolean;
  trialEndsAt: Date | string | null;
  currentPeriodEnd: Date | string | null;
  graceDays: number;
  suspendedAt: Date | string | null;
  cancelledAt: Date | string | null;
}

export interface LicenceState {
  phase: LicencePhase;
  readOnly: boolean;
  readOnlyReason: ReadOnlyReason | null;
  /** The instant the term ends (first instant after the last valid day); null when open-ended. */
  endsAt: string | null;
  /** The instant the workspace becomes read-only; null when open-ended. */
  graceEndsAt: string | null;
  /** Whole days until `endsAt`, counting today; 0 once it has passed; null when open-ended. */
  daysRemaining: number | null;
  /** Whole days of grace left; null outside the grace period. */
  graceDaysRemaining: number | null;
}

function toDate(value: Date | string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** The term's effective end: a trial ends at `trialEndsAt`, a paid licence at `currentPeriodEnd`. */
export function licenceEndsAt(input: Pick<LicenceStateInput, "isTrial" | "trialEndsAt" | "currentPeriodEnd">): Date | null {
  return toDate(input.isTrial ? input.trialEndsAt : input.currentPeriodEnd);
}

/**
 * The licence rule. Read-only when any of these holds:
 *
 *   · Lean2Automate paused it (`suspendedAt`) — a pause wins over every date;
 *   · it was cancelled;
 *   · the term has an end and `now ≥ end + graceDays × 24 h`.
 *
 * The start date never gates access: a licence that starts next month labels
 * the term, and the workspace keeps working until the END plus grace.
 * No subscription row at all is phase "none", and writable.
 *
 * A trial stays phase "trial" up to its end (the banner counts it down from
 * `daysRemaining`); "expiring" is the last {@link LICENCE_WARNING_DAYS} days of
 * a PAID term.
 */
export function licenceState(input: LicenceStateInput | null | undefined, now: Date = new Date()): LicenceState {
  if (!input || input.status === "none") {
    return { phase: "none", readOnly: false, readOnlyReason: null, endsAt: null, graceEndsAt: null, daysRemaining: null, graceDaysRemaining: null };
  }
  const t = now.getTime();
  const endsAt = licenceEndsAt(input);
  const graceEndsAt = endsAt ? new Date(endsAt.getTime() + Math.max(0, input.graceDays) * DAY_MS) : null;
  const inGrace = endsAt !== null && graceEndsAt !== null && t >= endsAt.getTime() && t < graceEndsAt.getTime();
  const base = {
    endsAt: endsAt ? endsAt.toISOString() : null,
    graceEndsAt: graceEndsAt ? graceEndsAt.toISOString() : null,
    daysRemaining: endsAt ? Math.max(0, Math.ceil((endsAt.getTime() - t) / DAY_MS)) : null,
    graceDaysRemaining: inGrace && graceEndsAt ? Math.max(0, Math.ceil((graceEndsAt.getTime() - t) / DAY_MS)) : null,
  };

  if (toDate(input.suspendedAt) || input.status === "suspended") return { ...base, phase: "paused", readOnly: true, readOnlyReason: "paused" };
  if (input.status === "cancelled") return { ...base, phase: "cancelled", readOnly: true, readOnlyReason: "cancelled" };
  if (!endsAt || !graceEndsAt) return { ...base, phase: input.isTrial ? "trial" : "active", readOnly: false, readOnlyReason: null };
  if (t >= graceEndsAt.getTime()) return { ...base, phase: "expired", readOnly: true, readOnlyReason: "expired" };
  if (t >= endsAt.getTime()) return { ...base, phase: "grace", readOnly: false, readOnlyReason: null };
  if (input.isTrial) return { ...base, phase: "trial", readOnly: false, readOnlyReason: null };
  if (endsAt.getTime() - t <= LICENCE_WARNING_DAYS * DAY_MS) return { ...base, phase: "expiring", readOnly: false, readOnlyReason: null };
  return { ...base, phase: "active", readOnly: false, readOnlyReason: null };
}

// ---------------------------------------------------------------------------
// Dates — calendar days in a time zone, with no dependency beyond Intl
// ---------------------------------------------------------------------------

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

function parseDateOnly(date: string): [number, number, number] {
  const match = DATE_PATTERN.exec(date);
  if (!match) throw new RangeError(`Expected a YYYY-MM-DD date, got "${date}"`);
  return [Number(match[1]), Number(match[2]), Number(match[3])];
}

function formatDateOnly(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

function zonedParts(instant: Date, timeZone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  let formatter = zoneFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    zoneFormatters.set(timeZone, formatter);
  }
  const parts = formatter.formatToParts(instant);
  const get = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return { year: get("year"), month: get("month"), day: get("day"), hour: get("hour") % 24, minute: get("minute"), second: get("second") };
}

/** Milliseconds the zone is ahead of UTC at that instant (IST: +19 800 000). */
function zoneOffsetMs(instant: Date, timeZone: string): number {
  const p = zonedParts(instant, timeZone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(instant.getTime() / 1000) * 1000;
}

/** The calendar date an instant falls on in a time zone, "YYYY-MM-DD". */
export function dateInZone(instant: Date | string, timeZone: string): string {
  const p = zonedParts(instant instanceof Date ? instant : new Date(instant), timeZone);
  return formatDateOnly(p.year, p.month, p.day);
}

/** Today in a time zone. */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return dateInZone(now, timeZone);
}

/**
 * The first instant of a calendar day in a time zone.
 *
 * Usually local midnight. On the few days a zone skips midnight for daylight
 * saving (Santiago, Havana), the day starts at the first instant that exists,
 * which is what "start of D" has to mean there.
 */
export function startOfDayIn(date: string, timeZone: string): Date {
  const [year, month, day] = parseDateOnly(date);
  const guess = Date.UTC(year, month - 1, day);
  const first = guess - zoneOffsetMs(new Date(guess), timeZone);
  const second = guess - zoneOffsetMs(new Date(first), timeZone);
  const onTheDay = [first, second].filter((c) => dateInZone(new Date(c), timeZone) === date).sort((a, b) => a - b);
  return new Date(onTheDay[0] ?? second);
}

/** `endsOn = D` → the stored end: the first instant of D+1 in the zone. */
export function endsAtFromDate(endsOn: string, timeZone: string): Date {
  return startOfDayIn(addDays(endsOn, 1), timeZone);
}

/** The stored end → the last valid day, `endsAt − 1 ms` as a date in the zone. */
export function endsOnFromInstant(endsAt: Date | string, timeZone: string): string {
  const instant = endsAt instanceof Date ? endsAt : new Date(endsAt);
  return dateInZone(new Date(instant.getTime() - 1), timeZone);
}

/** Calendar arithmetic on a "YYYY-MM-DD" date. */
export function addDays(date: string, days: number): string {
  const [year, month, day] = parseDateOnly(date);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

export type CalendarPeriod = { months: number } | { years: number } | { days: number };

/**
 * Adds calendar months, years or days. Months and years clamp to the end of a
 * shorter month: 31 Jan + 1 month is 28 Feb (29 in a leap year), never 3 Mar.
 */
export function addPeriod(date: string, period: CalendarPeriod): string {
  if ("days" in period) return addDays(date, period.days);
  const months = "months" in period ? period.months : period.years * 12;
  const [year, month, day] = parseDateOnly(date);
  const index = month - 1 + months;
  const targetYear = year + Math.floor(index / 12);
  const targetMonth = ((index % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(targetYear, targetMonth + 1, 0)).getUTCDate();
  return formatDateOnly(targetYear, targetMonth + 1, Math.min(day, lastDay));
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-30" → "30 Sep 2026": the form every licence notice and message uses. */
export function formatLicenceDate(date: string): string {
  const [year, month, day] = parseDateOnly(date);
  return `${day} ${MONTHS_SHORT[month - 1]} ${year}`;
}

/** How long a term runs. `{ days: N }` is N calendar days including the first. */
export type LicenceTerm = { endsOn: string } | { months: number } | { years: number } | { days: number } | { openEnded: true };

/** The last valid day of a term starting on `startsOn`; null when open-ended. */
export function termEndsOn(startsOn: string, term: LicenceTerm): string | null {
  if ("openEnded" in term) return null;
  if ("endsOn" in term) return term.endsOn;
  return addDays(addPeriod(startsOn, term), -1);
}

export type RenewFrom = "previous_end" | "today";
export type RenewPeriod = { months: number } | { years: number } | { days: number } | { endsOn: string };

export interface RenewTermInput {
  /** The old term's last valid day; null when it had no end. */
  previousEndsOn: string | null;
  /** True once the old term is past its grace (it has been, or would be, read-only on dates). */
  previousLapsed: boolean;
  from?: RenewFrom;
  period: RenewPeriod;
  /** Today in the workspace's zone. */
  today: string;
}

/**
 * The new term a renewal records.
 *
 *   1. The base: `previous_end` is the day after the old last day, `today` is
 *      today. Unless told, the base continues from the previous end while the
 *      old term is still in term or in grace, and starts today once it has
 *      lapsed (or never had an end) — nobody pays for days they were locked.
 *   2. `startsOn` is the base.
 *   3. `endsOn` is `base + period − 1 day`, or the explicit `endsOn`.
 *
 * The caller refuses `startsOn > endsOn`.
 */
export function renewTerm(input: RenewTermInput): { startsOn: string; endsOn: string } {
  const continueFrom = input.previousEndsOn ? addDays(input.previousEndsOn, 1) : null;
  let base: string;
  if (input.from === "previous_end") base = continueFrom ?? input.today;
  else if (input.from === "today") base = input.today;
  else base = continueFrom && !input.previousLapsed ? continueFrom : input.today;
  const endsOn = "endsOn" in input.period ? input.period.endsOn : addDays(addPeriod(base, input.period), -1);
  return { startsOn: base, endsOn };
}

// ---------------------------------------------------------------------------
// Licence invoices — the one state mapping
// ---------------------------------------------------------------------------

export const LICENCE_INVOICE_KINDS = ["invoice", "debit_note", "credit_note"] as const;
export type LicenceInvoiceKind = (typeof LICENCE_INVOICE_KINDS)[number];

export const LICENCE_INVOICE_STATES = ["due", "overdue", "paid", "credited", "cancelled", "issued"] as const;
export type LicenceInvoiceState = (typeof LICENCE_INVOICE_STATES)[number];
export const licenceInvoiceStateSchema = z.enum(LICENCE_INVOICE_STATES);

/** INV / DBN / CRN, as the billing workspace numbers them. */
export type LicenceDocType = "INV" | "DBN" | "CRN";

export function licenceInvoiceKind(docType: LicenceDocType | string | null): LicenceInvoiceKind {
  if (docType === "CRN") return "credit_note";
  if (docType === "DBN") return "debit_note";
  return "invoice";
}

export interface LicenceInvoiceStateInput {
  docType: LicenceDocType | string | null;
  status: string;
  totalMinor: number;
  paidMinor: number;
  creditedMinor: number;
  balanceMinor: number;
  isOverdue: boolean;
}

/**
 * What a client is told about one of Lean2Automate's invoices to it. In order:
 *
 *   1. a credit note is "issued" — it is a document, not something owed;
 *   2. cancelled is "cancelled";
 *   3. credited in full is "credited" — never "paid", which would tell the
 *      client money arrived when it did not;
 *   4. nothing left and something paid is "paid";
 *   5. past its due date is "overdue";
 *   6. otherwise "due". A part payment or part credit is still due, with the
 *      paid and credited amounts shown beside it.
 */
export function licenceInvoiceState(input: LicenceInvoiceStateInput): LicenceInvoiceState {
  if (input.docType === "CRN") return "issued";
  if (input.status === "cancelled") return "cancelled";
  if (input.creditedMinor >= input.totalMinor) return "credited";
  if (input.balanceMinor <= 0 && input.paidMinor > 0) return "paid";
  if (input.isOverdue) return "overdue";
  return "due";
}

// ---------------------------------------------------------------------------
// Errors a client can meet
// ---------------------------------------------------------------------------

/** `details` of a 409 SEAT_LIMIT_REACHED. */
export interface SeatLimitReachedDetails {
  limit: number;
  used: number;
}

/** `details` of a 402 WORKSPACE_SUSPENDED. */
export interface WorkspaceSuspendedDetails {
  /** The pause reason a platform owner wrote, when there is one. */
  reason: string | null;
  subscriptionStatus: string;
  readOnlyReason: ReadOnlyReason;
  /** The licence's last valid day, when it ran out. */
  endedOn: string | null;
}

// ---------------------------------------------------------------------------
// Contracts: what a workspace sees about itself
// ---------------------------------------------------------------------------

export const licenceViewSchema = z.object({
  phase: licencePhaseSchema,
  readOnly: z.boolean(),
  readOnlyReason: z.enum(READ_ONLY_REASONS).nullable(),
  isTrial: z.boolean(),
  /** First day of the term, in the workspace's zone. */
  startsOn: dateOnlySchema.nullable(),
  /** Last valid day; null when open-ended. */
  endsOn: dateOnlySchema.nullable(),
  endsAt: timestampSchema.nullable(),
  graceDays: z.number().int(),
  /** Last writable day of the grace period. */
  graceEndsOn: dateOnlySchema.nullable(),
  /** The day it becomes read-only. */
  readOnlyOn: dateOnlySchema.nullable(),
  graceEndsAt: timestampSchema.nullable(),
  daysRemaining: z.number().int().nullable(),
  graceDaysRemaining: z.number().int().nullable(),
});
export type LicenceView = z.infer<typeof licenceViewSchema>;

/** Staff seats. Portal contacts and API keys never count. */
export const seatUsageSchema = z.object({
  /** Active memberships. */
  active: z.number().int(),
  /** Unexpired pending invitations to people who are not already active members. */
  pending: z.number().int(),
  /** active + pending: what the cap is checked against. */
  used: z.number().int(),
  /** The enforced cap; null means none. */
  limit: z.number().int().nullable(),
});
export type SeatUsage = z.infer<typeof seatUsageSchema>;

/** What a workspace's plan grants it (console and tenant). `maxStaffUsers` is the ENFORCED cap. */
export const entitlementsSchema = z.object({
  plan: z.string(),
  planName: z.string(),
  features: z.array(z.string()),
  maxStaffUsers: z.number().int().nullable(),
  maxClients: z.number().int().nullable(),
  showsPoweredBy: z.boolean(),
  status: subscriptionStatusSchema,
  trialEndsAt: z.string().nullable(),
  serviceable: z.boolean(),
});
export type EntitlementsDto = z.infer<typeof entitlementsSchema>;

/** GET /api/v1/subscription. */
export const subscriptionResponseSchema = entitlementsSchema.extend({
  /** Why it was paused, in the words a platform owner chose. */
  statusReason: z.string().nullable(),
  licence: licenceViewSchema,
  seats: seatUsageSchema,
});
export type SubscriptionResponse = z.infer<typeof subscriptionResponseSchema>;

// ---------------------------------------------------------------------------
// Contracts: the console (/api/platform/v1)
// ---------------------------------------------------------------------------

export const PLATFORM_ROLES = ["support", "owner"] as const;
export type PlatformRoleKey = (typeof PLATFORM_ROLES)[number];
export const platformMeSchema = z.object({ userId: z.string(), role: z.enum(PLATFORM_ROLES) });

export const platformPlanSchema = z.object({
  key: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  priceMinor: z.number().int().nullable(),
  currency: z.string(),
  interval: z.string(),
  trialDays: z.number().int(),
  entitlements: z.array(z.string()),
  /** Pre-fills the console's seat field; the enforced cap is the subscription's own. */
  maxStaffUsers: z.number().int().nullable(),
  maxClients: z.number().int().nullable(),
  isPublic: z.boolean(),
});
export type PlatformPlan = z.infer<typeof platformPlanSchema>;

export const workspaceRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  plan: z.string(),
  status: subscriptionStatusSchema,
  trialEndsAt: z.string().nullable(),
  staffUsers: z.number().int(),
  createdAt: z.string(),
  suspendedAt: z.string().nullable(),
  statusReason: z.string().nullable(),
  priceMinor: z.number().int().nullable(),
  currency: z.string().nullable(),
  isTrial: z.boolean(),
  licenceStartsOn: dateOnlySchema.nullable(),
  licenceEndsOn: dateOnlySchema.nullable(),
  graceDays: z.number().int(),
  graceEndsOn: dateOnlySchema.nullable(),
  phase: licencePhaseSchema,
  readOnly: z.boolean(),
  daysRemaining: z.number().int().nullable(),
  maxStaffUsers: z.number().int().nullable(),
  seats: seatUsageSchema,
  /** The address an unaccepted owner invitation went to (a provisioned workspace before its first Admin joins). */
  pendingOwnerEmail: z.string().nullable(),
  billingClientId: z.string().nullable(),
  isBillingWorkspace: z.boolean(),
});
export type WorkspaceRowDto = z.infer<typeof workspaceRowSchema>;

/** How long a term runs. Exactly one form. */
export const licenceTermSchema = z.union([
  z.strictObject({ endsOn: dateOnlySchema }),
  z.strictObject({ months: z.number().int().min(1).max(120) }),
  z.strictObject({ years: z.number().int().min(1).max(10) }),
  z.strictObject({ days: z.number().int().min(1).max(3650) }),
  z.strictObject({ openEnded: z.literal(true) }),
]);

/**
 * PATCH /workspaces/:orgId/licence. No defaults: an absent key is unchanged.
 *
 * Setting `startsOn` or `endsOn` CONVERTS the term to a paid licence unless
 * `isTrial: true` is passed with it, which extends (or makes) a trial instead.
 * `endsOn: null` is an open-ended paid licence; `maxStaffUsers: null` lifts the cap.
 */
export const updateLicenceInputSchema = z.object({
  planKey: z.string().trim().min(1).max(40).optional(),
  priceMinor: minorSchema.min(0).nullable().optional(),
  isTrial: z.boolean().optional(),
  startsOn: dateOnlySchema.optional(),
  endsOn: dateOnlySchema.nullable().optional(),
  graceDays: z.number().int().min(0).max(365).optional(),
  maxStaffUsers: z.number().int().min(1).max(100_000).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export type UpdateLicenceInput = z.infer<typeof updateLicenceInputSchema>;

export const renewPeriodSchema = z.union([
  z.strictObject({ months: z.number().int().min(1).max(120) }),
  z.strictObject({ years: z.number().int().min(1).max(10) }),
  z.strictObject({ days: z.number().int().min(1).max(3650) }),
  z.strictObject({ endsOn: dateOnlySchema }),
]);

/** POST /workspaces/:orgId/licence/renew. See {@link renewTerm} for `from`. */
export const renewLicenceInputSchema = z.object({
  from: z.enum(["previous_end", "today"]).optional(),
  period: renewPeriodSchema,
  note: z.string().max(2000).nullable().optional(),
});
export type RenewLicenceInput = z.infer<typeof renewLicenceInputSchema>;

/**
 * POST /workspaces/:orgId/plan. On a TRIAL the server needs `term` (which
 * converts it to a paid licence) or `keepTrial: true`, and answers 422
 * otherwise. A plan change never lifts a pause.
 */
export const setPlanInputSchema = z.object({
  plan: z.string().min(1),
  priceMinor: minorSchema.min(0).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
  /** Restart a trial of the plan's length from today. */
  startTrial: z.boolean().optional(),
  term: licenceTermSchema.optional(),
  keepTrial: z.boolean().optional(),
});
export type SetPlanInput = z.infer<typeof setPlanInputSchema>;

export const suspendWorkspaceInputSchema = z.object({ reason: z.string().min(1).max(500) });

/** POST /api/platform/v1/workspaces — Lean2Automate creates a workspace for a customer. */
export const provisionWorkspaceInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  country: z.string().length(2).default("IN"),
  baseCurrency: currencyCodeSchema.default("INR"),
  timezone: timezoneSchema.default("Asia/Kolkata"),
  /** The first Admin: invited as the workspace's Owner. */
  ownerName: z.string().trim().min(1).max(120),
  ownerEmail: emailSchema,
  planKey: z.string().trim().min(1).max(40),
  priceMinor: minorSchema.min(0).nullable().optional(),
  /** Defaults to today in `timezone`. */
  startsOn: dateOnlySchema.optional(),
  term: licenceTermSchema,
  graceDays: z.number().int().min(0).max(365).default(DEFAULT_GRACE_DAYS),
  /** Null: no cap. */
  maxStaffUsers: z.number().int().min(1).max(100_000).nullable(),
  note: z.string().max(2000).nullable().optional(),
});
export type ProvisionWorkspaceInput = z.infer<typeof provisionWorkspaceInputSchema>;

export const provisionWorkspaceResultSchema = z.object({
  orgId: z.string(),
  slug: z.string(),
  invitationExpiresAt: timestampSchema,
});
export type ProvisionWorkspaceResult = z.infer<typeof provisionWorkspaceResultSchema>;

/** POST /workspaces/:orgId/owner-invitation. No body resends; a new `email` replaces the address. */
export const replaceOwnerInvitationInputSchema = z.object({
  email: emailSchema.optional(),
  name: z.string().trim().min(1).max(120).optional(),
});
export type ReplaceOwnerInvitationInput = z.infer<typeof replaceOwnerInvitationInputSchema>;

export const ownerInvitationResultSchema = z.object({
  email: z.string(),
  invitationExpiresAt: timestampSchema,
});

/** One row of Lean2Automate's own trail (GET /api/platform/v1/audit). */
export const platformAuditEventSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  actorUserId: z.string().nullable(),
  actorName: z.string(),
  action: z.string(),
  targetOrgId: z.string().nullable(),
  targetOrgName: z.string().nullable(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  summary: z.string(),
  changes: z.record(z.string(), z.unknown()),
  metadata: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
});
export type PlatformAuditEventDto = z.infer<typeof platformAuditEventSchema>;

export const platformAuditQuerySchema = z.object({
  orgId: uuidSchema.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------------------
// Contracts: licence billing (R5–R7)
// ---------------------------------------------------------------------------

export const billingWorkspaceSchema = z.object({
  id: z.string(),
  name: z.string(),
  gstin: z.string().nullable(),
  /** Whether the signed-in platform admin is an active member there. */
  adminIsMember: z.boolean(),
  /** invoices:write there. */
  canRaise: z.boolean(),
  /** payments:record there. */
  canRecordPayment: z.boolean(),
  /** The permission the client-create route needs there. */
  canCreateClient: z.boolean(),
});

/** GET/PUT /api/platform/v1/billing. */
export const billingSettingsSchema = z.object({
  billingOrgId: z.string().nullable(),
  licenceSacCode: z.string().nullable(),
  licenceTaxRateBps: z.number().int(),
  licencePaymentTermsDays: z.number().int(),
  billingWorkspace: billingWorkspaceSchema.nullable(),
  /** Things to fix before raising, e.g. a billing workspace with no GSTIN. */
  warnings: z.array(z.string()),
});
export type BillingSettingsDto = z.infer<typeof billingSettingsSchema>;

/** PUT /api/platform/v1/billing. Absent keys keep their current value. */
export const billingSettingsInputSchema = z.object({
  billingOrgId: uuidSchema.nullable(),
  licenceSacCode: z.string().trim().regex(/^\d{4,8}$/, "A SAC is 4 to 8 digits").nullable().optional(),
  licenceTaxRateBps: z.number().int().min(0).max(10_000).optional(),
  licencePaymentTermsDays: z.number().int().min(0).max(365).optional(),
});
export type BillingSettingsInput = z.infer<typeof billingSettingsInputSchema>;

/** PUT /api/platform/v1/workspaces/:orgId/billing-client. Null unlinks. */
export const linkBillingClientInputSchema = z.object({ clientId: uuidSchema.nullable() });

/** A licence's link to its client in the billing workspace, with what Rule 46 still needs. */
export const billingLinkSchema = z.object({
  orgId: z.string(),
  clientId: z.string().nullable(),
  clientName: z.string().nullable(),
  rule46Gaps: z.array(z.string()),
});
export type BillingLinkDto = z.infer<typeof billingLinkSchema>;

/** GET /api/platform/v1/billing/clients?q= */
export const billingClientOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  gstin: z.string().nullable(),
  stateCode: z.string().nullable(),
  /** Workspaces already linked to this client. */
  linkedOrgIds: z.array(z.string()),
});

/** POST /api/platform/v1/billing/clients — company, GSTIN, client and billing contact in one go. */
export const createBillingClientInputSchema = z.object({
  legalName: z.string().trim().min(1).max(200),
  displayName: z.string().trim().min(1).max(200).optional(),
  gstin: z.string().trim().toUpperCase().length(15).optional(),
  addressLine1: z.string().trim().min(1).max(200),
  addressLine2: z.string().trim().max(200).optional(),
  city: z.string().trim().min(1).max(100),
  stateCode: z.string().trim().min(2).max(2),
  pincode: z.string().trim().min(3).max(12),
  country: z.string().length(2),
  contactName: z.string().trim().min(1).max(120),
  contactEmail: emailSchema,
  /** Link the new client to this workspace's licence once it exists. */
  linkToOrgId: uuidSchema.optional(),
});
export type CreateBillingClientInput = z.infer<typeof createBillingClientInputSchema>;

export const createBillingClientResultSchema = z.object({
  clientId: z.string(),
  rule46Gaps: z.array(z.string()),
  linked: z.boolean(),
});

/**
 * POST /api/platform/v1/licences/:orgId/invoices (needs an Idempotency-Key).
 * `issue` and `send` carry no zod default: the route treats a missing value
 * as true, and ignores `send` when `issue` is false.
 */
export const raiseLicenceInvoiceInputSchema = z.object({
  periodStart: dateOnlySchema,
  periodEnd: dateOnlySchema,
  description: z.string().trim().max(500).optional(),
  sacCode: z.string().trim().regex(/^\d{4,8}$/, "A SAC is 4 to 8 digits").optional(),
  /** Excluding GST. */
  amountMinor: minorSchema.min(1),
  taxRateBps: z.number().int().min(0).max(10_000).optional(),
  invoiceDate: dateOnlySchema.optional(),
  dueDate: dateOnlySchema.optional(),
  paymentTermsDays: z.number().int().min(0).max(365).optional(),
  poNumber: z.string().trim().max(60).optional(),
  notes: z.string().max(2000).optional(),
  issue: z.boolean().optional(),
  send: z.boolean().optional(),
});
export type RaiseLicenceInvoiceInput = z.infer<typeof raiseLicenceInvoiceInputSchema>;

export const raiseLicenceInvoiceResultSchema = z.object({
  invoiceId: z.string(),
  /** Null for a draft: numbers are allocated only at issue. */
  number: z.string().nullable(),
  status: z.string(),
  issued: z.boolean(),
  sent: z.boolean(),
  /** For a draft: where to finish it in the billing workspace. */
  deepLink: z.string().nullable(),
});

/** One of Lean2Automate's documents to a workspace, as that workspace sees it. Money is null without financials:view. */
export const licenceInvoiceSchema = z.object({
  id: z.string(),
  externalInvoiceId: z.string(),
  kind: z.enum(LICENCE_INVOICE_KINDS),
  /** On a credit note, the invoice it reduces. */
  sourceInvoiceNumber: z.string().nullable(),
  number: z.string(),
  invoiceDate: dateOnlySchema,
  dueDate: dateOnlySchema.nullable(),
  periodStart: dateOnlySchema.nullable(),
  periodEnd: dateOnlySchema.nullable(),
  totalMinor: minorSchema.nullable(),
  paidMinor: minorSchema.nullable(),
  creditedMinor: minorSchema.nullable(),
  balanceMinor: minorSchema.nullable(),
  currency: z.string(),
  state: licenceInvoiceStateSchema,
  pdfUrl: z.string().nullable(),
  pdfStatus: z.enum(["ready", "preparing"]),
});
export type LicenceInvoiceDto = z.infer<typeof licenceInvoiceSchema>;

/** The console's view: the same row with whose it is. */
export const platformLicenceInvoiceSchema = licenceInvoiceSchema.extend({
  orgId: z.string(),
  orgName: z.string(),
  /**
   * The client in the billing workspace the document was issued to, as it
   * stands there now (null if it cannot be read). Not the licence's current
   * link, which may have moved since: recording a payment needs this one.
   */
  clientId: z.string().nullable(),
});
export type PlatformLicenceInvoiceDto = z.infer<typeof platformLicenceInvoiceSchema>;
